import type { Commit } from "../lib/types";
import type { SandboxState } from "../lib/sandbox-engine";

interface Props {
  state: SandboxState;
}

// Branch lane colors. `main` is always white; others cycle through this palette.
const PALETTE = ["#2D9BD6", "#9B6FE0", "#34D399", "#F59E0B", "#F472B6", "#60A5FA"];

const STEP = 110; // horizontal distance per commit column
const GAP = 90; // vertical distance per branch lane
const OFFSET_X = 70;
const TOP = 56;

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function labelWidth(text: string): number {
  return text.length * 6.7 + 18;
}

function edgePath(p: { x: number; y: number }, me: { x: number; y: number }): string {
  if (p.y === me.y) return `M ${p.x} ${p.y} L ${me.x} ${me.y}`;
  const midX = p.x + (me.x - p.x) * 0.55;
  return `M ${p.x} ${p.y} C ${midX} ${p.y}, ${midX} ${me.y}, ${me.x} ${me.y}`;
}

function reachableSet(state: SandboxState): Set<string> {
  const roots = [...Object.values(state.branches), ...Object.values(state.tags)];
  if (state.detached) roots.push(state.head);
  const set = new Set<string>();
  const stack = [...roots];
  while (stack.length) {
    const id = stack.pop()!;
    if (set.has(id)) continue;
    set.add(id);
    const c = state.commits.find((x) => x.id === id);
    if (c?.parent) stack.push(c.parent);
    if (c?.parent2) stack.push(c.parent2);
  }
  return set;
}

export function SandboxGraph({ state }: Props) {
  const { commits } = state;

  // Assign each distinct branch a lane. `main` is pinned to the bottom lane.
  const branchOrder: string[] = [];
  commits.forEach((c) => {
    if (!branchOrder.includes(c.branch)) branchOrder.push(c.branch);
  });
  // Stable lane ordering: main first (=> bottom), then first-seen order.
  branchOrder.sort((a, b) => {
    if (a === "main") return -1;
    if (b === "main") return 1;
    return 0;
  });
  const laneIndex: Record<string, number> = {};
  branchOrder.forEach((b, i) => (laneIndex[b] = i));
  const laneColor: Record<string, string> = {};
  branchOrder.forEach((b, i) => {
    laneColor[b] = b === "main" ? "#FFFFFF" : PALETTE[(i - (branchOrder.includes("main") ? 1 : 0) + PALETTE.length) % PALETTE.length];
  });

  const numLanes = Math.max(branchOrder.length, 1);
  const laneY = (branch: string) => {
    const idx = laneIndex[branch] ?? 0;
    return TOP + (numLanes - 1 - idx) * GAP;
  };

  const positions: Record<string, { x: number; y: number; color: string }> = {};
  commits.forEach((c) => {
    positions[c.id] = {
      x: OFFSET_X + c.x * STEP,
      y: laneY(c.branch),
      color: laneColor[c.branch] ?? "#FFFFFF",
    };
  });

  const maxX = commits.length ? Math.max(...commits.map((c) => c.x)) : 0;
  const width = OFFSET_X + (maxX + 1) * STEP + 120;
  const height = TOP + numLanes * GAP + 40;

  const reachable = reachableSet(state);
  const headCommitId = state.detached ? state.head : state.branches[state.head];

  // Refs (branches + tags) grouped by the commit they point at, so labels stack.
  type Ref = { name: string; color: string; isHead: boolean; isTag: boolean };
  const refsByCommit: Record<string, Ref[]> = {};
  Object.entries(state.branches).forEach(([name, id]) => {
    (refsByCommit[id] ??= []).push({
      name,
      color: laneColor[name] ?? "#FFFFFF",
      isHead: !state.detached && state.head === name,
      isTag: false,
    });
  });
  Object.entries(state.tags).forEach(([name, id]) => {
    (refsByCommit[id] ??= []).push({ name: `⛬ ${name}`, color: "#FBBF24", isHead: false, isTag: true });
  });

  return (
    <div className="gf-graph">
      <div className="gf-graph__head">
        <h3>Local repository</h3>
        <div className="gf-graph__head-meta">
          <span>
            branch:{" "}
            <b>{state.detached ? `(detached ${state.head.slice(0, 7)})` : state.head}</b>
          </span>
          <span>HEAD → {(headCommitId || "—").slice(0, 7)}</span>
        </div>
      </div>

      <div className="gf-graph__body" style={{ overflowX: "auto" }}>
        {commits.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
            No commits yet. Stage a file and run <code>git commit -m "…"</code> to start the graph.
          </div>
        ) : (
          <svg
            className="gf-graph__svg"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
          >
            {/* Lane guide lines + labels */}
            {branchOrder.map((b) => {
              const y = laneY(b);
              return (
                <g key={`lane-${b}`}>
                  <line
                    x1={0}
                    y1={y}
                    x2={width}
                    y2={y}
                    stroke="rgba(255,255,255,0.07)"
                    strokeWidth="1"
                    strokeDasharray="2 4"
                  />
                  <text
                    x={16}
                    y={y + 4}
                    fill={b === "main" ? "rgba(255,255,255,0.4)" : laneColor[b]}
                    fontSize="11"
                    fontFamily="ui-monospace, Menlo"
                    opacity={0.75}
                  >
                    {b}
                  </text>
                </g>
              );
            })}

            {/* First-parent edges */}
            {commits.map((c) => {
              if (!c.parent) return null;
              const p = positions[c.parent];
              const me = positions[c.id];
              if (!p || !me) return null;
              const dim = !reachable.has(c.id);
              return (
                <path
                  key={`e-${c.id}`}
                  d={edgePath(p, me)}
                  stroke={me.color}
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={dim ? "5 4" : undefined}
                  opacity={dim ? 0.3 : 1}
                />
              );
            })}

            {/* Second-parent (merge) edges */}
            {commits.map((c) => {
              if (!c.parent2) return null;
              const p = positions[c.parent2];
              const me = positions[c.id];
              if (!p || !me) return null;
              return (
                <path
                  key={`e2-${c.id}`}
                  d={edgePath(p, me)}
                  stroke={me.color}
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  fill="none"
                  strokeLinecap="round"
                  opacity={0.8}
                />
              );
            })}

            {/* Commit nodes */}
            {commits.map((c: Commit) => {
              const pos = positions[c.id];
              const dim = !reachable.has(c.id);
              const onMain = c.branch === "main";
              return (
                <g key={c.id} opacity={dim ? 0.32 : 1}>
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r="11"
                    fill={pos.color}
                    stroke={onMain ? "#fff" : pos.color}
                    strokeWidth="2"
                  />
                  {c.parent2 && (
                    <circle cx={pos.x} cy={pos.y} r="5.5" fill="none" stroke={onMain ? "#001847" : "#fff"} strokeWidth="1.5" />
                  )}
                  <text
                    x={pos.x}
                    y={pos.y + 26}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.5)"
                    fontSize="10"
                    fontFamily="ui-monospace, Menlo"
                  >
                    {c.id.slice(0, 7)}
                  </text>
                  <text
                    x={pos.x}
                    y={pos.y + 39}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.78)"
                    fontSize="10.5"
                    fontFamily="Raleway, sans-serif"
                  >
                    {truncate(c.msg, 18)}
                  </text>
                </g>
              );
            })}

            {/* Ref labels (branches + tags), stacked above their commit */}
            {Object.entries(refsByCommit).map(([commitId, refs]) => {
              const pos = positions[commitId];
              if (!pos) return null;
              return refs.map((r, idx) => {
                const text = r.isHead ? `HEAD → ${r.name}` : r.name;
                const w = labelWidth(text);
                const ly = pos.y - 20 - idx * 24;
                return (
                  <g key={`ref-${commitId}-${r.name}`}>
                    <rect
                      x={pos.x - w / 2}
                      y={ly - 11}
                      width={w}
                      height="22"
                      rx="11"
                      fill={r.isHead ? r.color : "transparent"}
                      stroke={r.color}
                      strokeWidth="1.5"
                      strokeDasharray={r.isTag ? "3 2" : undefined}
                    />
                    <text
                      x={pos.x}
                      y={ly + 4}
                      textAnchor="middle"
                      fill={r.isHead ? (r.color === "#FFFFFF" ? "#001847" : "#fff") : r.color}
                      fontSize="10.5"
                      fontFamily="ui-monospace, Menlo"
                      fontWeight="600"
                    >
                      {text}
                    </text>
                  </g>
                );
              });
            })}

            {/* Detached HEAD pointer */}
            {state.detached && positions[state.head] && (
              <g>
                {(() => {
                  const pos = positions[state.head];
                  const text = "HEAD (detached)";
                  const w = labelWidth(text);
                  const ly = pos.y - 20;
                  return (
                    <>
                      <rect x={pos.x - w / 2} y={ly - 11} width={w} height="22" rx="11" fill="#F87171" stroke="#F87171" />
                      <text x={pos.x} y={ly + 4} textAnchor="middle" fill="#fff" fontSize="10.5" fontFamily="ui-monospace, Menlo" fontWeight="600">
                        {text}
                      </text>
                    </>
                  );
                })()}
              </g>
            )}
          </svg>
        )}
      </div>

      <div className="gf-graph__legend">
        <div className="gf-graph__legend-item">
          <div className="gf-graph__legend-dot" style={{ background: "#fff" }}></div>
          <span>main</span>
        </div>
        {branchOrder
          .filter((b) => b !== "main")
          .slice(0, 4)
          .map((b) => (
            <div className="gf-graph__legend-item" key={`lg-${b}`}>
              <div className="gf-graph__legend-dot" style={{ background: laneColor[b] }}></div>
              <span>{b}</span>
            </div>
          ))}
        <div className="gf-graph__legend-item" style={{ marginLeft: "auto" }}>
          <span style={{ fontFamily: "ui-monospace, Menlo" }}>{commits.length} commits</span>
        </div>
      </div>
    </div>
  );
}
